'use client'

import { ErrorMessage, PaymentButton, PaymentCard, PaymentHeader, PaymentMethods } from '@/components/ui/payment-card'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useEffect, useState } from 'react'
import { ApiOrderCreateParams } from "./api-interface/payments/create/interface"

function HomeContent() {
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [orderId, setOrderId] = useState('')
  const [buyerName, setBuyerName] = useState('')
  const [notifyUrl, setNotifyUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('alipay')
  const searchParams = useSearchParams()
  const router = useRouter()

  // 支付方式配置
  const paymentMethods = [
    {
      id: 'alipay',
      name: '支付宝',
      description: '10亿人都在用,真安全,更方便',
      icon: '/images/alipay-logo.png',
      color: 'blue',
      recommended: true,
    },
    {
      id: 'wechat',
      name: '微信支付',
      description: '微信扫码支付,安全便捷',
      icon: '微',
      color: 'green',
    },
    {
      id: 'unionpay',
      name: '银联支付',
      description: '银联卡支付,全国通用',
      icon: '银',
      color: 'blue',
    },
  ]

  useEffect(() => {
    // 从URL参数获取支付信息
    // 注意：签名验证已在middleware中完成，这里只需要获取显示信息
    const urlAmount = searchParams.get('amount')
    const urlDescription = searchParams.get('description')
    const urlOrderId = searchParams.get('order_id')
    const urlPaymentMethod = searchParams.get('payment_method')
    const hasSignature = searchParams.get('sign')
    const urlBuyerName = searchParams.get('buyer_name')
    const urlNotifyUrl = searchParams.get('notify_url')

    if (urlAmount) {
      setAmount(urlAmount)
    }
    if (urlDescription) {
      setDescription(urlDescription)
    }
    if (urlOrderId) {
      setOrderId(urlOrderId)
    }
    if (urlBuyerName) {
      setBuyerName(urlBuyerName)
    }
    if (urlNotifyUrl) {
      setNotifyUrl(urlNotifyUrl)
    }
    if (urlPaymentMethod && paymentMethods.some(m => m.id === urlPaymentMethod)) {
      setSelectedPaymentMethod(urlPaymentMethod)
    }

    // 如果有签名参数，说明已经通过middleware验证
    if (hasSignature) {
      console.log('收银台页面: 签名验证已通过middleware完成')
    }
  }, [searchParams])

  /**
   * 处理支付表单提交
   * 调用支付创建API，成功后跳转到支付结果页面
   */
  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault()
    }
    setLoading(true)
    setError('')

    try {
      const paymentCreateParams: ApiOrderCreateParams = {
        out_trade_no: orderId,
        total_amount: amount,
        subject: description,
        notify_url: notifyUrl,
        buyer_name: buyerName,
        payment_method: 'alipay',
      }
      const body = JSON.stringify(paymentCreateParams)
      // 调用支付创建API
      const response = await fetch('/api/payments/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: body,
      })

      const data = await response.json()
      const paymentUrl = data.data

      if (data.success) {
        // 新窗口打开支付链接
        window.open(paymentUrl, '_blank')
        // // 当前窗口先显示支付结果页
        // const paymentResultParams = new URLSearchParams({
        //   amount: amount,
        //   description: description,
        //   buyerName: buyerName,
        // })
        // window.location.href = `${process.env.NEXT_PUBLIC_BASE_URL}/payment-result?${paymentResultParams.toString()}`
        // 关闭当前窗口
        setTimeout(() => {
          window.close()
        }, 800)
      } else {
        setError(data.message || '创建支付订单失败')
      }

      // if (data.success) {
      //   // 构建支付结果页面URL
      //   const resultUrl = new URL(PATHS.PAGES.PAYMENT_RESULT, window.location.origin)
      //   resultUrl.searchParams.set('orderNumber', data.data.orderNumber)
      //   resultUrl.searchParams.set('paymentMethod', selectedPaymentMethod)
      //   resultUrl.searchParams.set('amount', amount)
      //   resultUrl.searchParams.set('description', description)
      //
      //   // 添加支付结果信息
      //   if (data.data.paymentUrl) {
      //     resultUrl.searchParams.set('paymentUrl', data.data.paymentUrl)
      //   }
      //   if (data.data.qrCode) {
      //     resultUrl.searchParams.set('qrCode', data.data.qrCode)
      //   }
      //   if (data.data.expireTime) {
      //     resultUrl.searchParams.set('expireTime', data.data.expireTime)
      //   }
      //   if (data.data.shouldRedirectToApp) {
      //     resultUrl.searchParams.set('shouldRedirectToApp', 'true')
      //   }
      //   if (data.data.appScheme) {
      //     resultUrl.searchParams.set('appScheme', data.data.appScheme)
      //   }
      //
      //   // 保留原始参数
      //   if (searchParams.get('returnUrl')) {
      //     resultUrl.searchParams.set('returnUrl', searchParams.get('returnUrl')!)
      //   }
      //   if (searchParams.get('notifyUrl')) {
      //     resultUrl.searchParams.set('notifyUrl', searchParams.get('notifyUrl')!)
      //   }
      //
      //   // 跳转到支付结果页面
      //   router.push(resultUrl.toString())
      // } else {
      //   setError(data.message || '创建支付订单失败')
      // }
    } catch (error) {
      console.error('支付请求失败:', error)
      setError('网络错误，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  const getButtonText = () => {
    if (loading) return '处理中...'

    const methodName = selectedPaymentMethod === 'alipay' ? '支付宝' :
      selectedPaymentMethod === 'wechat' ? '微信' : '银联'
    return `${methodName}支付¥${amount || '0.00'}`
  }

  return (
    <div className="h-screen-real w-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full px-4 py-6">
        {/* 支付卡片容器 - 使用flex-1让卡片填充所有可用空间 */}
        <PaymentCard className="flex-1 flex flex-col">
          {/* 支付状态头部 */}
          <PaymentHeader
            amount={amount}
            desc={description}
            statusText={`${buyerName} 正在向 hakimi.ai 支付订单`}
          />

          {/* 支付方式选择 - 可滚动区域 */}
          <PaymentMethods
            methods={paymentMethods}
            selectedMethod={selectedPaymentMethod}
            onSelect={setSelectedPaymentMethod}
          />

          {/*填充空隙*/}
          <div className="flex-1"/>

          {/* 支付按钮 - 固定在底部 */}
          <div className="px-6 py-6 flex-shrink-0">
            <PaymentButton
              onClick={handleSubmit}
              disabled={loading || !amount}
              loading={loading}
              text={getButtonText()}
              paymentMethod={selectedPaymentMethod}
              amount={amount}
            />
          </div>
        </PaymentCard>
      </div>

      {/* 错误提示 */}
      {error && <ErrorMessage message={error}/>}

      {/* 备案信息 - 固定间距 */}
      <div className="mt-6 text-center flex-shrink-0">
        <a
          href="https://beian.miit.gov.cn/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          京ICP备2025133283号
        </a>
      </div>
    </div>
  )
}

export default function HomePage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen-real bg-gray-50 flex items-center justify-center">
        <div className="text-lg">加载中...</div>
      </div>
    }>
      <HomeContent/>
    </Suspense>
  )
}
